import { RendererRuntime } from '../runtime';
import { LayoutBase } from './LayoutBase';
import Layout, { LayoutItemMap } from './index';
import { useLayerStore } from '@/store/use-layer-store';

export default class extends LayoutBase {
  getSize(): { width: number; height: number } {
    const node = this.dataItem as IWidget;
    const {
      w = 0,
      h = 0,
      flex_direction = 'disabled',
      gap = 0,
      children = []
    } = node;
    if (!children?.length) return { width: w, height: h };

    // 有自动布局时按子元素实际尺寸汇总（含 gap），图组等动态尺寸变化时嵌套组间距才能正确更新
    if (flex_direction === 'row' || flex_direction === 'column') {
      const _gap = Number(gap);
      let totalWidth = 0;
      let totalHeight = 0;
      let maxChildWidth = 0;
      let maxChildHeight = 0;
      children.forEach((layout: ILayout, index: number) => {
        const { width = 0, height = 0 } =
          this.getChildLayoutIns(layout)?.getSize() ?? {};
        if (index > 0) {
          totalWidth += flex_direction === 'row' ? _gap : 0;
          totalHeight += flex_direction === 'column' ? _gap : 0;
        }
        totalWidth += flex_direction === 'row' ? width : 0;
        totalHeight += flex_direction === 'column' ? height : 0;
        maxChildWidth = Math.max(maxChildWidth, width);
        maxChildHeight = Math.max(maxChildHeight, height);
      });
      if (flex_direction === 'row') {
        return { width: totalWidth, height: Math.max(maxChildHeight, h) };
      }
      return { width: Math.max(maxChildWidth, w), height: totalHeight };
    }

    // 无自动布局但有子元素时，按子项包围盒计算，使嵌套组（如组1）尺寸随子元素变化
    let minX = 0;
    let minY = 0;
    let maxX = -Infinity;
    let maxY = -Infinity;
    children.forEach((layout: ILayout) => {
      const { x: cx = 0, y: cy = 0, ref } = layout;
      const { width = 0, height = 0 } =
        this.getChildLayoutIns(layout)?.getSize() ?? {};

      let adjustedCx = cx;
      // 居中/右对齐的图组，cx 是对齐锚点而非内容左边缘，需修正
      // dataItems 的 key 带 @ 前缀，直接用 ref
      const dataItem = ref ? this.runtime.dataItems[ref] : undefined;
      if (
        dataItem?.tagType === 'DataItemImageNumber' &&
        (dataItem as IDataItemImageNumber).align &&
        (dataItem as IDataItemImageNumber).align !== 'left'
      ) {
        adjustedCx = 0;
      }

      minX = Math.min(minX, adjustedCx);
      minY = Math.min(minY, cy);
      maxX = Math.max(maxX, adjustedCx + width);
      maxY = Math.max(maxY, cy + height);
    });
    const cw = maxX === -Infinity ? w : maxX - minX;
    const ch = maxY === -Infinity ? h : maxY - minY;
    return { width: Math.max(cw, w), height: Math.max(ch, h) };
  }
  getChildLayoutIns(layout: ILayout) {
    const { ref, _id, _scale = 1, _alpha = 1 } = layout;
    const { runtime, renderer } = this;
    const dataItemData = runtime.dataItems[ref];
    if (!dataItemData) return;
    const Obj = LayoutItemMap[dataItemData.tagType as 'Image'];
    if (!Obj) return;

    const obj = new Obj(
      renderer,
      dataItemData,
      {
        x: 0,
        y: 0,
        offsetX: 0,
        offsetY: 0
      },
      _id,
      _scale,
      _alpha
    );
    return obj;
  }
  private alignWidgetIfNeed(widget: IWidget) {
    const {
      flex_direction = 'disabled',
      w = 0,
      h = 0,
      justify_content = 'flex-start',
      align_items = 'flex-start',
      gap = 0,
      children = []
    } = widget;

    if (flex_direction !== 'row' && flex_direction !== 'column') return;

    // 根据对齐方式获取新的子元素xy坐标
    // 获取子元素的size
    const _gap = Number(gap);
    const totalSize = { width: 0, height: 0 };
    const sizeMap: Record<string, { width: number; height: number }> = {};
    children.forEach((layout, index) => {
      const { width = 0, height = 0 } =
        this.getChildLayoutIns(layout)?.getSize() ?? {};
      sizeMap[layout.ref] = { width, height };
      // 当没有宽和高时，代表不渲染
      if (!width || !height) return;
      totalSize.width += width + (index > 0 ? _gap : 0);
      totalSize.height += height + (index > 0 ? _gap : 0);
    });

    if (flex_direction == 'row') {
      let x =
        justify_content == 'flex-end'
          ? w - totalSize.width
          : justify_content == 'center'
            ? (w - totalSize.width) / 2
            : 0;
      children.forEach((item) => {
        const { width = 0, height = 0 } = sizeMap[item.ref] ?? {};
        // 当没有宽和高时，代表不渲染
        if (!width || !height) return;
        item.x = x;
        item.y =
          align_items == 'flex-end'
            ? h - height
            : align_items == 'center'
              ? (h - height) / 2
              : 0;
        // 获取下一个元素的x
        x += _gap + width;

        // 如果是图组元素，且使用了对齐，需要修正x坐标
        const dataItem = this.runtime.getDataItem<IDataItemImageNumber>(
          item.ref
        );
        if (dataItem?.tagType == 'DataItemImageNumber') {
          if (dataItem.align == 'center') {
            x += -width / 2;
          } else if (dataItem.align == 'right') {
            x += -width;
          }
        }
      });
    } else {
      let y =
        justify_content == 'flex-end'
          ? h - totalSize.height
          : justify_content == 'center'
            ? (h - totalSize.height) / 2
            : 0;
      children.forEach((item) => {
        const { width = 0, height = 0 } = sizeMap[item.ref] ?? {};
        // 当没有宽和高时，代表不渲染
        if (!width || !height) return;
        item.y = y;
        item.x =
          align_items == 'flex-end'
            ? w - width
            : align_items == 'center'
              ? (w - width) / 2
              : 0;
        y += _gap + height;
      });
    }
  }
  private setSize(layouts: ILayout[]) {
    const { runtime } = this;
    let minX = 0;
    let minY = 0;
    let maxX = -Infinity;
    let maxY = -Infinity;

    const widgetName = (this.dataItem as IWidget).name ?? this.name;

    layouts.forEach((layout) => {
      const { ref, _id } = layout;
      const refKey = ref.replace('@', '');

      // 检查是否为条件切换 Slot，如果是则遍历 Slot 内所有子元素取最大包围盒
      const dataItem = runtime.dataItems[ref];
      if (dataItem?.tagType === 'Slot' && (dataItem as ISlot).type === 'condition') {
        const slotChildren = ((dataItem as ISlot).children ?? []).filter(
          (item) => item.tagType === 'Item'
        );
        slotChildren.forEach((child) => {
          const childRefKey = child.ref?.replace('@', '') ?? '';
          const childStateKey = RendererRuntime.generateStateKey(childRefKey, child._id ?? '');
          const childState = runtime.state[childStateKey];
          if (!childState) return;
          const { offsetX = 0, offsetY = 0, width = 0, height = 0 } = childState;
          minX = Math.min(minX, offsetX);
          minY = Math.min(minY, offsetY);
          maxX = Math.max(maxX, offsetX + width);
          maxY = Math.max(maxY, offsetY + height);
        });
        return;
      }

      const name = RendererRuntime.generateStateKey(refKey, _id);
      const item = runtime.state[name];
      if (!item) return;

      let { offsetX, offsetY, width, height } = item;

      // 用带 @ 的 ref 查 dataItems（dataItems 的 key 带 @ 前缀）
      const tagType = dataItem?.tagType;
      const align = tagType === 'DataItemImageNumber' ? (dataItem as IDataItemImageNumber).align : undefined;

      // 居中/右对齐的图组，offsetX 是对齐锚点位置，不是内容左边缘。
      // 成组时 calcLayersRectFromAttrs 已将 leftX 对齐到内容左边缘，
      // setRelativeAttrs 后 child.x（即 offsetX）= 锚点到内容左边缘的距离。
      // 内容左边缘在组内始终是 0，所以直接归零。
      if (tagType === 'DataItemImageNumber' && align && align !== 'left') {
        offsetX = 0;
      }

      minX = Math.min(minX, offsetX);
      minY = Math.min(minY, offsetY);
      maxX = Math.max(maxX, offsetX + width);
      maxY = Math.max(maxY, offsetY + height);
    });

    const finalW = maxX == -Infinity ? 100 : maxX - minX;
    const finalH = maxY == -Infinity ? 100 : maxY - minY;

    this.setRuntimeData({
      width: finalW,
      height: finalH,
      rotation: 0
    });
  }
  draw(): void {
    const { posi, renderer, dataItem } = this;
    const node = dataItem as IWidget;
    this.alignWidgetIfNeed(node);
    let childern = node.children;

    // 组动效条件切换：通过 shapeId 从 layerStore 获取条件数据，实时判断显示哪个子元素
    const shapeId = this._layoutId;
    if (childern?.length > 1 && shapeId) {
      const layer = useLayerStore().findLayer(shapeId)?.layer;
      const widgetConditions = layer?.attrs?.widgetConditions as Condition[] | undefined;

      if (widgetConditions?.length) {
        // 子元素 ID 按反转顺序（与结构树一致）
        const childIds = [...(layer?.children ?? [])].reverse().map((c: any) => c.id);

        // 将编辑器 Condition 转为渲染层 ICondition 格式
        const operatorMap: Record<string, string> = {
          LessThan: '&lt;',
          LessThanOrEqual: '&lt;=',
          Equal: '==',
          MoreThan: '>',
          MoreThanOrEqual: '>='
        };
        const toICondition = (cond: Condition): ICondition => ({
          tagType: 'Condition',
          name: cond.id,
          logic_op: (cond.logicalOperator ?? 'and') as 'and' | 'or',
          children: cond.children.map((compare) => ({
            tagType: 'Compare',
            name: compare.id,
            logic_op: (compare.logicalOperator ?? 'and') as 'and' | 'or',
            type: compare.type === 'when' ? 'data' : 'click',
            children: (compare.children ?? []).map((param) => ({
              tagType: 'Param',
              name: param.id,
              value: param.whenValue ?? 0,
              source: param.whenSourceKey ?? '',
              operators: operatorMap[param.whenOperator ?? 'Equal'] ?? '=='
            })) as ICompareParam[]
          })) as ICompare[]
        });

        // 找到第一个满足条件的子元素 ID
        let activeId = childIds[childIds.length - 1];
        for (let i = 0; i < widgetConditions.length && i < childIds.length - 1; i++) {
          const cond = widgetConditions[i];
          if (!cond?.children?.length) continue;
          if (this.condition(toICondition(cond))) {
            activeId = childIds[i];
            break;
          }
        }

        // 标记隐藏状态，并清除 x/y（与容器子元素一致，对齐左上角）
        childern = childern.map(child => ({
          ...child,
          x: 0,
          y: 0,
          _conditionHidden: child._id !== activeId
        }));
      }
    }

    new Layout(renderer).draw(childern, posi);
    if (childern?.length) this.setSize(childern);
  }
}
